import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import CardSelectionController from 'src/components/Dashboard/CardSelectionController';
import {
  buildChartViewKey, getCardSelectionState, getChartSummaryData, selectedKeysToggleKey,
} from 'src/components/Dashboard/helpers/common';
import useFilteredChartData from 'src/components/Dashboard/helpers/useFilteredChartData';
import Loading from 'src/components/Loading';
import { MeterChartTooltip } from 'src/components/Portfolio/PortfolioShow/ChartTooltip/MeterChartTooltip';
import TradeChartTooltip from 'src/components/Portfolio/PortfolioShow/ChartTooltip/TradeChartTooltip';
import { APIConfig } from 'src/config';
import Chart from 'src/enosikit/components/Chart';
import Cards from 'src/enosikit/components/Chart/components/Cards';
import { meterContent, tradeContent } from 'src/enosikit/components/Chart/helpers/cards';
import Pagination from 'src/enosikit/components/Pagination/components/Pagination';
import { getTotalCarbonEmission } from 'src/lib/carbondata/helpers';
import FlashesStore from 'src/stores/FlashesStore';
import {
  DATA_AGGREGATE_BY_PORTFOLIO, DATA_AGGREGATE_BY_PROPERTY,
  DATA_GROUP_BY_COUNTERPARTY, DATA_GROUP_BY_TRADE_TYPE,
  DEFAULT_MAX_VISIBLE_CARDS_COUNT,
  EXPORTS, IMPORTS, SOURCE,
  SOURCE_HISTORIAN, SOURCE_TRADES,
  UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY,
} from 'src/util/constants';
import { buildOtherSubSeriesLabelGetter } from 'src/util/i18n/constants';
import buildPaginationMetadata from 'src/util/pagination';

import {
  buildCardsData, buildChartData, buildMeterViewCardLabel, buildTradeViewCardLabel,
} from './helpers/portfolioShowChart';

/**
 * Maximum number of cards that can be selected before showing a warning to the user.
 *
 * This is a known limitation based on browser URL length constraints. When card selections
 * are stored in URL parameters (for state persistence and sharing), selecting too many cards
 * can exceed browser URL limits, particularly Firefox's 65,536 character limit.
 * Firefox's 65,536 character limit is the most restrictive constraint
 * actively-used modern browsers.
 *
 * The limit of 1750 cards was chosen as a conservative threshold that:
 * - Provides a reasonable buffer before reaching browser limits
 * - Accounts for URL encoding overhead and other URL parameters
 * - Balances user flexibility with technical constraints
 * - Based on ~37 characters per UUID in URL-encoded form
 *
 * When this limit is exceeded:
 * - A warning flash message is displayed to inform the user of potential issues
 * - The selection is still allowed to proceed (non-blocking warning)
 * - Users can make their own choice about whether to continue selecting more cards
 *
 * This is an intentional design decision to balance user experience with technical constraints.
 * @constant {number}
 * @see {@link https://medium.com/@python-javascript-php-html-css/recognizing-the-maximum-url-length-for-different-browsers-ff275903b970}
 */
const MAX_CARD_SELECTION_COUNT = 1750;

/**
 * Adapter that maps the positional arguments invoked by `enosikit`'s `ChartTooltip`
 * (via the `tooltipLabelFunc` prop on `ChartTrade`) to the props object expected by
 * `TradeChartTooltip`. This isolates the Portfolio-specific trade tooltip UI from the
 * generic chart orchestration logic in `enosikit`, which has no knowledge of trade
 * domain concepts.
 *
 * Defined at module scope (rather than inside the component body) so the function
 * reference is stable across renders, which is required for `React.memo` on
 * `ChartTrade` to avoid unnecessary re-renders.
 * @param {object} data Buy/sell trade tooltip data for the hovered timestamp.
 * @param {string} unitLabel Unit display label.
 * @param {object} chartView Chart aggregation and grouping options.
 * @param {string} unit Chart unit.
 * @returns {React.ReactElement} Rendered `TradeChartTooltip`.
 */
export const renderTradeChartTooltip = (data, unitLabel, chartView, unit) => (
  <TradeChartTooltip
    chartView={chartView}
    data={data}
    unit={unit}
    unitLabel={unitLabel}
  />
);

/**
 * Adapter that maps the positional arguments invoked by `enosikit`'s `ChartTooltip`
 * (via the `tooltipLabelFunc` prop on `ChartMeter`) to the props object expected by
 * `MeterChartTooltip`. This isolates the Portfolio-specific meter tooltip UI from the
 * generic chart orchestration logic in `enosikit`, which has no knowledge of meter
 * domain concepts.
 *
 * Defined at module scope (rather than inside the component body) so the function
 * reference is stable across renders, which is required for `React.memo` on
 * `ChartMeter` to avoid unnecessary re-renders.
 * @param {object} tooltipData Consumed/generated meter tooltip data for the hovered timestamp.
 * @param {string} unitLabel Unit display label.
 * @param {object} chartView Chart aggregation and grouping options.
 * @param {string} unit Chart unit.
 * @returns {React.ReactElement} Rendered `MeterChartTooltip`.
 */
export const renderMeterChartTooltip = (tooltipData, unitLabel, chartView, unit) => (
  <MeterChartTooltip
    chartView={chartView}
    tooltipData={tooltipData}
    unit={unit}
    unitLabel={unitLabel}
  />
);

/**
 * Returns the PortfolioShowChart component
 * @param {object} props
 * @param {object} props.mainData MainData for the portfolio dashboard.
 * @param {object} props.chartView Grouping and aggregation options for the chart.
 * @param {DATA_AGGREGATE_BY_PROPERTY | DATA_AGGREGATE_BY_PORTFOLIO} props.chartView.aggregateBy
 * @param {DATA_GROUP_BY_COUNTERPARTY | DATA_GROUP_BY_TRADE_TYPE} props.chartView.groupBy
 * @param {Function} props.controlSetStateFunc Set the meter or the trade view.
 * @param {string} props.historianAggregation Aggregation for the chart.
 * @param {SOURCE_HISTORIAN | SOURCE_TRADES} props.source MainData for the portfolio dashboard
 * @param {object} props.timespan - An object defining the start and end dates for the timespan.
 * @param {Function} props.timespanUpdateFunc Gives the ability to update the timespan.
 * @param {string} props.timezone - The timezone for the chart.
 * @param {string} props.tradeAggregation The aggregation method for the trade data.
 * @param {UNIT_ENERGY| UNIT_CARBON | UNIT_CURRENCY} props.unit The unit for the chart.
 * @param {object} props.selectedKeys The selected keys for the chart.
 * @param {Function} props.setSelectedKeysFunc The function to set the selected keys for the chart.
 * @param {string} [props.noDataMessage] - Message to display when the user cancelled
 * a request and there is no current data available.
 * @returns {React.ReactComponentElement} PortfolioShowChart
 */
function PortfolioShowChart({ noDataMessage = null, ...props }) {
  const {
    chartView, controlSetStateFunc, mainData, historianAggregation,
    source, timespan, timespanUpdateFunc, timezone,
    tradeAggregation, unit, selectedKeys, setSelectedKeysFunc,
  } = props;

  const intl = useIntl();
  const chartRef = useRef(null);

  const [chartSize, setChartSize] = useState({ height: 0, width: 0 });
  const [hoverKeys, setHoverKeys] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [tooltipTimestamp, setTooltipTimestamp] = useState(null);

  const tooltipUpdateFunc = useCallback((timestamp) => {
    setTooltipTimestamp((currentTimestamp) => (
      currentTimestamp?.ts === timestamp.ts ? currentTimestamp : timestamp
    ));
  }, []);

  // Create a chart view identifier that includes all properties of chartView
  const chartViewKey = buildChartViewKey(chartView, source);
  const mode = APIConfig().MODE;
  const meterView = source === SOURCE_HISTORIAN;
  const tradeView = source === SOURCE_TRADES;
  const aggByProperty = chartView?.aggregateBy === DATA_AGGREGATE_BY_PROPERTY;

  useEffect(() => {
    setCurrentPage(1);
    setHoverKeys([]);
  }, [chartViewKey]);

  /**
   * Set the boundary for the bar chart.
   * @returns {void}
   */
  const setChartBounds = useCallback(() => {
    if (!chartRef.current) {
      return;
    }

    let { clientHeight: height, clientWidth: width } = chartRef.current;

    const {
      paddingTop, paddingBottom, paddingLeft, paddingRight,
    } = window.getComputedStyle(chartRef.current);

    if (paddingTop !== '') { height -= parseFloat(paddingTop); }
    if (paddingBottom !== '') { height -= parseFloat(paddingBottom); }
    if (paddingLeft !== '') { width -= parseFloat(paddingLeft); }
    if (paddingRight !== '') { width -= parseFloat(paddingRight); }

    setChartSize({ height, width });
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    setHoverKeys([]);
  }, []);

  const chartData = useMemo(
    () => (mainData ? buildChartData(intl, meterView, mainData, timezone, chartView) : null),
    [intl, meterView, mainData, timezone, chartView],
  );

  const chartSummaryData = useMemo(
    () => (mainData ? getChartSummaryData(mainData) : { buy: {}, sell: {} }),
    [mainData],
  );

  const cardsData = useMemo(
    () => buildCardsData(meterView, chartData, chartView),
    [meterView, chartData, chartView],
  );

  // activePage is currentPage clamped to the valid range by buildPaginationMetadata.
  // This prevents a "flash" of empty data when totalPages decreases but currentPage
  // state hasn't been reset yet by the useEffect.
  const {
    pageCount: totalPages,
    currentPage: activePage,
    paginatedItems: pageCards,
  } = useMemo(
    () => buildPaginationMetadata(cardsData, currentPage, DEFAULT_MAX_VISIBLE_CARDS_COUNT),
    [cardsData, currentPage],
  );

  const {
    finalSelectedKeys,
    hasCurrentViewSelectedKeys,
    hasAnySelectedKeys,
    enableClearBtn,
    cardsDataAvailable,
  } = useMemo(
    () => getCardSelectionState(selectedKeys, chartViewKey, cardsData),
    [selectedKeys, chartViewKey, cardsData],
  );

  const meterOtherLabel = useMemo(() => intl.formatMessage({
    id: 'portfolio.portfolio_show.chart.cards.other.label',
    defaultMessage: 'Other',
  }), [intl]);

  const getLabelForOtherSubSeriesFunc = useCallback(
    buildOtherSubSeriesLabelGetter(intl),
    [intl],
  );

  const { visibleCards, filteredChartData } = useFilteredChartData({
    pageCards,
    finalSelectedKeys,
    totalPages,
    cardsData,
    chartData,
    meterView,
    meterOtherLabel,
    getLabelForOtherSubSeriesFunc,
  });

  useEffect(() => {
    // Victory sets the SVG width attribute from the `width` prop. When the
    // actual container width changes (sidebar toggles, CSS transitions, layout
    // shifts) but the prop stays stale, the browser scales the entire SVG to
    // fit — making axis labels and tooltip text appear larger or smaller even
    // though their font-size attribute is unchanged.
    //
    // The previous window 'resize' listener only fired on browser-window
    // resizes, missing container-only changes. ResizeObserver fires whenever
    // the observed element's dimensions change for any reason, keeping the
    // width prop in sync and preventing the SVG scaling mismatch.
    const node = chartRef.current;
    if (!node) return undefined;

    // Measure immediately on mount so the first render has accurate dimensions.
    setChartBounds();

    const resizeObserver = new ResizeObserver(() => {
      setChartBounds();
    });
    resizeObserver.observe(node);
    return () => resizeObserver.disconnect();
  }, [setChartBounds]);

  /**
   * Handles the hover state for the cards in the PortfolioShowChart component.
   * @param {string} key The unique identifier of the card being hovered over.
   * @param {boolean} hover Indicates whether the card is being hovered over or not.
   * @returns {void}
   */
  const cardsHoverFunc = useCallback((key, hover) => {
    setHoverKeys((prevHoverKeys) => {
      const hasKey = prevHoverKeys.includes(key);
      if (hover && !hasKey) return [...prevHoverKeys, key];
      if (!hover && hasKey) return prevHoverKeys.filter((k) => k !== key);
      return prevHoverKeys;
    });
  }, []);

  /**
   * Handles the selection state for the cards in the PortfolioShowChart component.
   *
   * This function toggles the selection state of a card identified by its key. When a user
   * attempts to select more than MAX_CARD_SELECTION_COUNT cards, a warning flash message
   * is displayed to inform them of potential browser URL length issues. However, the selection
   * is still allowed to proceed, giving users the choice to continue despite the warning.
   *
   * This is a known limitation and intentional design decision:
   * - Users receive a non-blocking warning when exceeding the recommended limit
   * - The selection proceeds anyway, respecting user choice
   * - Deselection always works without any warnings
   * @param {string} key The unique identifier of the card being selected or deselected.
   * @returns {void}
   * @see MAX_CARD_SELECTION_COUNT for details on the selection limit
   */
  const cardsSelectedFunc = useCallback((key) => {
    setSelectedKeysFunc((prevState) => {
      // Get current selected keys for this view
      const currentViewKeys = chartViewKey && prevState[chartViewKey]
        ? prevState[chartViewKey]
        : [];

      // Check if we're adding a new key (not removing)
      const addingKey = !currentViewKeys.includes(key);

      // If adding would exceed the limit, show warning but still allow selection
      if (addingKey && currentViewKeys.length >= MAX_CARD_SELECTION_COUNT) {
        const warningMessage = intl.formatMessage(
          {
            id: 'portfolio.portfolio_show.chart.selection_limit_exceeded.warning',
            defaultMessage: 'The card selection is too large ({currentCount}/{maxCount}) to be preserved when sharing a link to this dashboard in some browsers',
          },
          {
            currentCount: currentViewKeys.length,
            maxCount: MAX_CARD_SELECTION_COUNT,
          },
        );
        FlashesStore.flash(FlashesStore.WARNING, warningMessage);
      }

      return selectedKeysToggleKey(prevState, chartViewKey, key);
    });
  }, [chartViewKey, intl, setSelectedKeysFunc]);

  /**
   * Renders the content for a meter card.
   * @param {object} cardDatum An object containing the data for the meter card.
   * @param {object=} cardDatum.property The property that is part of the portfolio.
   * @param {object=} cardDatum.counterParty The counterparty.
   * @param {object=} cardDatum.counterPartyProperty The coutnerparty's property.
   * @param {object} cardDatum.buy The buy object for the meter card.
   * @param {object} cardDatum.sell The sell object for the meter card.
   * @returns {React.ReactElement|null} The rendered meter card content.
   */
  const meterViewCard = useCallback((cardDatum) => {
    const { property, buy, sell } = cardDatum;
    const { volume: buyVolume } = buy;
    const { volume: sellVolume } = sell;
    const cardLabel = buildMeterViewCardLabel(property, timespan, timezone, chartView);

    return meterContent(intl, cardLabel, buyVolume, sellVolume);
  }, [chartView, intl, timespan, timezone]);

  /**
   * Renders the content for a trade card.
   * @param {object} cardDatum An object containing the data for the trade card.
   * @param {object=} cardDatum.counterPartyUser Counterparty.
   * @param {object=} cardDatum.counterPartyProperty Counterparty's property (if visible).
   * @param {object=} cardDatum.property property object.
   * @param {object=} cardDatum.tradeType trade type for the card.
   * @returns {React.ReactElement} The rendered trade card content.
   */
  const tradeViewCard = useCallback((cardDatum) => {
    const { tradeType } = cardDatum;
    const cardLabel = buildTradeViewCardLabel(intl, cardDatum, chartView, timezone, timespan);

    return tradeContent(intl, tradeType, cardDatum, cardLabel);
  }, [chartView, intl, timespan, timezone]);

  /**
   * Handles the timespan update when a bar(in the chart) is clicked.
   * @returns {void}
   */
  const handleChartClick = useCallback(() => {
    if (tooltipTimestamp === null || historianAggregation.match(/^\d+S$/)) {
      return;
    }
    timespanUpdateFunc(tooltipTimestamp, tooltipTimestamp);
  }, [historianAggregation, timespanUpdateFunc, tooltipTimestamp]);

  // All hooks must be called before this guard.
  if (!mainData) {
    return <Loading />;
  }

  const { hasCounterfactual } = mainData;
  const totalCarbonEmission = getTotalCarbonEmission(mainData.buy?.data);
  const { buy: buySummary, sell: sellSummary } = chartSummaryData;

  const {
    ChartControls, ChartMeter, ChartSummary, ChartTrade,
  } = Chart;

  /**
   * Provides the control options for the chart in the PortfolioShowChart component.
   * @returns {object[]} An array of control option objects.
   */
  const buildControlOptions = () => {
    const typeOpts = {
      group: SOURCE,
      items: [{
        value: SOURCE_TRADES, label: <FormattedMessage id="portfolio.portfolio_show.chart.control.source.trades.title" defaultMessage="Trades" />, active: (source === SOURCE_TRADES), disabled: false,
      }, {
        value: SOURCE_HISTORIAN, label: <FormattedMessage id="portfolio.portfolio_show.chart.control.source.historian.title" defaultMessage="Meter" />, active: (source === SOURCE_HISTORIAN), disabled: false,
      }],
    };
    return [typeOpts];
  };

  return (
    <>
      <div className="mt-4 mb-4 card">
        <div className="card-body" ref={chartRef}>
          <div className="card-body d-flex justify-content-between">
            <div className="flex-fill justify-content-center order-2 order-xs-3 col-xs-12">
              <ChartControls
                buttons={buildControlOptions()}
                onButtonClick={controlSetStateFunc}
              />
            </div>
            <div className="flex-fill order-1 order-xs-1 col-xs-12">
              <ChartSummary
                align="left"
                category={IMPORTS}
                carbonData={totalCarbonEmission}
                counterfactual={buySummary.netDiffValue}
                hasCounterfactual={hasCounterfactual}
                mode={mode}
                tradedEnergy={buySummary.tradedEnergy}
                tradedValue={buySummary.tradedValue}
                untradedEnergy={buySummary.untradedVolume}
                untradedValue={buySummary.untradedValue}
                rebatedEnergy={buySummary.rebatedEnergy}
              />
            </div>
            <div className="flex-fill order-3 order-xs-2 col-xs-12">
              <ChartSummary
                align="right"
                category={EXPORTS}
                counterfactual={sellSummary.netDiffValue}
                hasCounterfactual={hasCounterfactual}
                mode={mode}
                tradedEnergy={sellSummary.tradedEnergy}
                tradedValue={sellSummary.tradedValue}
                untradedEnergy={sellSummary.untradedVolume}
                untradedValue={sellSummary.untradedValue}
                rebatedEnergy={sellSummary.rebatedEnergy}
              />
            </div>
          </div>
          {noDataMessage && (
            <div className="d-flex justify-content-center align-items-center">
              <div className="alert alert-warning">
                {noDataMessage}
              </div>
            </div>
          )}
          {tradeView && !noDataMessage && (
            <ChartTrade
              aggregation={tradeAggregation}
              chartView={chartView}
              handleChartClick={handleChartClick}
              hoverKeys={hoverKeys}
              selectedKeys={finalSelectedKeys}
              timespan={timespan}
              timezone={timezone}
              tooltipLabelFunc={renderTradeChartTooltip}
              tooltipUpdateFunc={tooltipUpdateFunc}
              tradeDataBuy={filteredChartData.buy}
              tradeDataSell={filteredChartData.sell}
              unit={unit}
              width={chartSize.width}
            />
          )}
          {meterView && !noDataMessage && (
            <ChartMeter
              aggregation={historianAggregation}
              chartView={chartView}
              data={filteredChartData}
              handleChartClick={handleChartClick}
              hoverKeys={hoverKeys}
              isByMeter={aggByProperty}
              selectedKeys={finalSelectedKeys}
              timespan={timespan}
              timezone={timezone}
              tooltipLabelFunc={renderMeterChartTooltip}
              tooltipUpdateFunc={tooltipUpdateFunc}
              unit={unit}
              width={chartSize.width}
            />
          )}
        </div>
      </div>
      {cardsDataAvailable && (
        <div className="d-flex justify-content-end mb-2">
          <CardSelectionController
            enableClearBtn={enableClearBtn}
            hasCurrentViewSelectedKeys={hasCurrentViewSelectedKeys}
            hasAnySelectedKeys={hasAnySelectedKeys}
            onClearCurrentSelectionFunc={() => setSelectedKeysFunc(
              (prev) => ({ ...prev, [chartViewKey]: [] }),
            )}
            onClearAllSelectionsFunc={() => setSelectedKeysFunc({})}
          />
        </div>
      )}

      {(meterView && cardsDataAvailable) && (
        <div className="position-relative">
          <Cards
            cardFunc={meterViewCard}
            data={visibleCards}
            hoverFunc={cardsHoverFunc}
            hoverKeys={hoverKeys}
            selectedFunc={cardsSelectedFunc}
            selectedKeys={finalSelectedKeys}
          />
          {totalPages > 1 && (
            <div className="d-flex justify-content-end mb-4">
              <Pagination
                ariaLabel={intl.formatMessage({ id: 'portfolio.portfolio_show.chart.cards.pagination.navigation.label', defaultMessage: 'Cards pagination navigation' })}
                currentPage={activePage}
                onPageChangeFunc={handlePageChange}
                totalPages={totalPages}
              />
            </div>
          )}
        </div>
      )}
      {(tradeView && cardsDataAvailable) && (
        <div className="position-relative">
          <Cards
            cardFunc={tradeViewCard}
            data={visibleCards}
            hoverFunc={cardsHoverFunc}
            hoverKeys={hoverKeys}
            selectedFunc={cardsSelectedFunc}
            selectedKeys={finalSelectedKeys}
          />
          {totalPages > 1 && (
            <div className="d-flex justify-content-end mb-4">
              <Pagination
                ariaLabel={intl.formatMessage({ id: 'portfolio.portfolio_show.chart.cards.pagination.navigation.label', defaultMessage: 'Cards pagination navigation' })}
                currentPage={activePage}
                onPageChangeFunc={handlePageChange}
                totalPages={totalPages}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}

PortfolioShowChart.propTypes = {
  chartView: PropTypes.shape({
    aggregateBy: PropTypes.oneOf([
      DATA_AGGREGATE_BY_PORTFOLIO,
      DATA_AGGREGATE_BY_PROPERTY,
    ]).isRequired,
    groupBy: PropTypes.oneOf([
      DATA_GROUP_BY_COUNTERPARTY,
      DATA_GROUP_BY_TRADE_TYPE,
    ]).isRequired,
  }).isRequired,
  controlSetStateFunc: PropTypes.func.isRequired,
  historianAggregation: PropTypes.string.isRequired,
  mainData: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  noDataMessage: PropTypes.string,
  source: PropTypes.oneOf([SOURCE_HISTORIAN, SOURCE_TRADES]).isRequired,
  selectedKeys: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  setSelectedKeysFunc: PropTypes.func.isRequired,
  timespan: PropTypes.shape({
    start: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    finish: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }).isRequired,
  timespanUpdateFunc: PropTypes.func.isRequired,
  timezone: PropTypes.string.isRequired,
  tradeAggregation: PropTypes.string.isRequired,
  unit: PropTypes.oneOf([UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY]).isRequired,
};

export default React.memo(PortfolioShowChart);
